import { Grid2, Skeleton } from "@mui/material";
import { DateTime } from "luxon";
import { forwardRef, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "../../app/helpers";
import { RootState } from "../../app/rootReducer";
import { filterVisitorDeskDemandUsers } from "../../features/Booking-Form/functions/visitor-registration.functions";
import { activeBookingFiltersForApi } from "../../features/Booking-Form/functions/booking-filter-state.functions";
import { BookingType } from "../../features/Booking-Form/typings/booking-inputs";
import { GetBookingLocationParams } from "../../features/Booking-Form/thunks/booking.thunks";
import { LocationAvailability } from "../../features/Booking-Form/typings/booking-location";
import { LocationInputs } from "../../features/Booking-Form/typings/inputs-location";
import { LocationInventory } from "../../features/FloorManager/typings/location-inventory";
import { setSettings } from "../../features/Login/slices/login.slice";
import { useRemoteFetchLocations } from "../../hooks/Remote/Location/useRemoteFetchLocations";
import { useRemoteFetchLocationAvailability } from "../../hooks/Remote/Location/useRemoteFetchLocationAvailability";
import { useRemoteSetUserSettings } from "../../hooks/Remote/User/useRemoteSetUserSettings";
import { transformBookingUsers } from "../BookingForm/functions/form-confirm.functions";
import { LocationCardMT } from "../LocationCard/LocationCardNew/LocationCardMT";
import { LocationNonPreferredCard } from "../LocationCard/LocationCardNonPreferred/location-card-non-preferred.component";

type P = {
  locationToState: (inputs: LocationInputs) => void;
};

/**
 * @description Component that renders the possible booking locations.
 * @version 0.1.0
 */

export const LocationsView = forwardRef<HTMLDivElement, P>(({ locationToState }, ref) => {
  const [allLocations, setAllLocations] = useState<LocationInventory[]>();
  const [bookingLocations, setBookingLocations] = useState<LocationAvailability[]>([]);
  const {
    userInformation: {
      sub,
      participatesInMobileWorking,
      company: {
        meta: { mobileWorkingEnabled }
      }
    },
    settings
  } = useSelector((state: RootState) => state.login);
  const { inputs } = useSelector((state: RootState) => state.booking);
  const dispatch = useDispatch();

  const { mutateAsync: fetchAllLocations, isLoading } = useRemoteFetchLocations();
  const { mutateAsync: fetchLocationAvailabilities, isLoading: bookingLocationIsLoading } =
    useRemoteFetchLocationAvailability();

  const { mutate: updateUserSettings, status: statusSetUserSettings } = useRemoteSetUserSettings();

  async function fetchAndAssignLocations() {
    if (inputs.usersBookedFor && !inputs.activityBasedBooking) {
      const usersForLocationAvailability =
        inputs.mode === BookingType.VISITOR
          ? filterVisitorDeskDemandUsers(inputs.usersBookedFor)
          : inputs.usersBookedFor;

      const bookingUser = transformBookingUsers(
        usersForLocationAvailability,
        sub,
        inputs,
        settings.timezone
      );

      const bookingFilterDto: GetBookingLocationParams = {
        users: bookingUser,
        startTime: inputs.bookingStart as string,
        endTime: inputs.bookingEnd as string,
        startDate: inputs.bookingFrom as string,
        endDate: inputs.bookingTo as string,
        bookingType: inputs.bookingType,
        weekdays: inputs.weekdays,
        frequence: inputs.frequence,
        interval: inputs.interval,
        specificDays: inputs.specificDays,
        bookingMonthDay: inputs.bookingMonthDay,
        bookingYearDay: inputs.bookingYearDay,
        ...(activeBookingFiltersForApi(inputs.bookingFilters)
          ? { bookingFilters: activeBookingFiltersForApi(inputs.bookingFilters) }
          : {})
      };

      fetchLocationAvailabilities(bookingFilterDto).then(r => setBookingLocations(r));
      fetchAllLocations().then(r => setAllLocations(r));
    }
  }

  const activeCardsToDisplay = useMemo(
    () =>
      bookingLocations?.filter(loc => {
        // if there is an external user, only display card which is not a mobile working card
        const hasExternalUsers = inputs.usersBookedFor?.some(
          user => user.isExternal === true || user.isVisitor === true
        );
        if (hasExternalUsers) return !loc.isMobileWorking;

        // check the company setting
        if (!mobileWorkingEnabled) return !loc.isMobileWorking;

        // check the user participate in mobile working
        if (!participatesInMobileWorking) return !loc.isMobileWorking;

        return true;
      }),
    [bookingLocations, participatesInMobileWorking, inputs.usersBookedFor, mobileWorkingEnabled]
  );

  // cards that cannot be selected
  const inactiveCardsToDisplay = useMemo(
    () =>
      allLocations
        ?.filter(
          loc =>
            // ids match or is mobile working
            !bookingLocations.some(locOld => locOld.id === loc.id || loc.isMobileWorking)
        )
        .filter(
          loc =>
            // is booking currently allowed
            loc.allowForBooking &&
            DateTime.fromISO(loc.bookingAllowedAt as string).valueOf() <=
              DateTime.fromISO(inputs.bookingFrom as string).valueOf()
        ),
    [allLocations, inputs.bookingFrom, bookingLocations]
  );

  function handleEnable(locationToEnable: LocationInventory) {
    const preferredLocations = settings.preferredLocations?.concat(locationToEnable.id.toString());
    const updatedSettings = {
      ...settings,
      preferredLocations
    };
    // update settings so they are updated when returning to first screen
    dispatch(
      setSettings({
        settings: updatedSettings
      })
    );
    updateUserSettings(updatedSettings);
  }

  // load locations on init
  useEffect(() => {
    if (!inputs || !sub || !settings.timezone) return;
    fetchAndAssignLocations().then();
  }, [
    sub,
    inputs.activityBasedBooking,
    inputs.isMobileWorking,
    inputs.bookingFrom,
    inputs.bookingTo,
    inputs.bookingStart,
    inputs.bookingEnd,
    inputs.bookingType,
    inputs.usersBookedFor,
    inputs.isMobileWorking,
    inputs.weekdays,
    inputs.frequence,
    inputs.interval,
    inputs.specificDays,
    inputs.bookingMonthDay,
    inputs.bookingYearDay,
    settings.timezone
  ]);

  // reload list when location is enabled
  useEffect(() => {
    if (statusSetUserSettings === "success") fetchAndAssignLocations().then();
  }, [statusSetUserSettings]);

  return (
    <Grid2
      container
      sx={{ minHeight: "15px", gap: 3 }}
      ref={ref}
      data-testid="location-card-list-container"
    >
      <>
        {/* location is loading */}
        {(isLoading || bookingLocationIsLoading) && <LocationPlaceholder />}

        {/* active locations */}
        {!isLoading &&
          !bookingLocationIsLoading &&
          activeCardsToDisplay?.map(loc => (
            <LocationCardMT key={loc.id} location={loc} locationToState={locationToState} />
          ))}

        {/* inactive locations */}
        {!isLoading &&
          !bookingLocationIsLoading &&
          bookingLocations &&
          inactiveCardsToDisplay?.map(nonPreferredLoc => (
            <LocationNonPreferredCard
              key={nonPreferredLoc.uid}
              location={nonPreferredLoc}
              onClickEnable={() => handleEnable(nonPreferredLoc)}
            />
          ))}
      </>
    </Grid2>
  );
});

const LocationPlaceholder = forwardRef<HTMLDivElement>((_, ref) => {
  return (
    <>
      <Skeleton ref={ref} variant="rectangular" width={400} height={193} />
      <Skeleton ref={ref} variant="rectangular" width={400} height={193} />
    </>
  );
});

export default LocationsView;
